import { FC, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useErrorModal } from "@/components/forms/errorHandling/useErrorModal";
import { useTranslation } from "react-i18next";
import GenericCrudTable from "@/components/tables/genericCrudTable";
import type { RowActionsSlot } from "@/components/tables/rowActions/rowActionsSlot";
import { getRecordMetadataColumns } from "@/components/tables/recordMetadataColumns";
import { List, ListItem, ListItemText, Tooltip } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import MapsHomeWorkIcon from "@mui/icons-material/MapsHomeWork";
import { api, apiRequest } from "@/helper/api";
import styles from "@/components/buildings/buildingTable.module.css";
import { useConfirmationModal } from "@/context/confirmationModalContext";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import Modal from "@/components/modal";
import FileUploadInput from "@/components/forms/inputs/upload";
import GenericNotification from "@/components/notification/genericNotification";
import { useSelector } from "react-redux";
import { RootState } from "@/components/state/store";
import { usePageHeading } from "@/helper/customHooks/usePageHeading";
import type { FileUploadRequestOptions } from "@/helper/customHooks/useFileUploadProgress";
import { buildingEntityAdapter } from "@/components/buildings/buildingEntityAdapter";

interface Props {
  title?: string;
  subjectId?: string;
  hideBackButton?: boolean;
  hideTableHeader?: boolean;
}

const BuildingTable: FC<Props> = ({
  subjectId: propSubjectId,
  hideBackButton,
  hideTableHeader,
}) => {
  const { handleError, errorModal } = useErrorModal();
  const [refreshTable, setRefreshTable] = useState<any>(false);
  const [companyName, setCompanyName] = useState<string>("");
  const [csvUploadModalOpen, setCsvUploadModalOpen] = useState<boolean>(false);
  const [csvUploadInProgress, setCsvUploadInProgress] = useState(false);
  const [entries, setEntries] = useState({
    created: [],
    updated: [],
    skipped: [],
  });
  const [entriesModalOpen, setEntriesModalOpen] = useState<boolean>(false);
  const [showNotification, setShowNotification] = useState<boolean>(false);
  const [notificationMessage, setNotificationMessage] = useState<string>("");
  const [notificationVariant, setNotificationVariant] = useState<
    "success" | "error" | "warning" | "info" | undefined
  >();
  const { t } = useTranslation();
  const { subjectId: urlSubjectId } = useParams() as {
    subjectId: string;
  };
  const { showConfirmationModalAsync } = useConfirmationModal();

  const user = useSelector((state: RootState) => state.login);

  const isDepartmentManager = user?.group_set.includes("manager") && false;

  const navigate = useNavigate();
  const hostname = window.location.hostname;
  const subdomain = hostname.split(".")[0];

  const additionalUrlParameters: any = {};
  const subjectId = propSubjectId || urlSubjectId;
  if (subjectId) {
    additionalUrlParameters["subject"] = subjectId;
  }

  useEffect(() => {
    if (subjectId) {
      api.genericApiRequest({
        entity: "subject",
        entityId: subjectId,
        method: "get",
        parametersToRender: { depth: "0" },
        successHandler: (res: any) => {
          setCompanyName(res.data.name);
        },
        failHandler: (error: any) => {
          handleError(error);
        },
      });
    }
  }, [subjectId]);

  const handleNavigateBack = () => {
    navigate("/admin/companyList");
  };

  const buildingRowActions: RowActionsSlot = (
    row,
    { standardActions, requestReload }
  ) =>
    subjectId || row.original.is_main_building
      ? standardActions
      : [
          ...standardActions,
          {
            key: "setMainBuilding",
            icon: <MapsHomeWorkIcon />,
            label: t("building_set_main_building", {
              building: row.original.name,
            }),
            onClick: () => setMainBuildingOnManager(row.original, requestReload),
          },
        ];

  const setMainBuildingOnManager = async (
    data: any,
    reloadTable: () => void
  ) => {
    const isConfirmed = await showConfirmationModalAsync(
      t("building_set_main_building_question", { building: data.name })
    );
    if (!isConfirmed) return;
    const result = { main_building: data.id };
    const config = {
      headers: {
        "Content-Type": "application/json",
      },
    };
    api.genericApiRequest({
      method: "put",
      entity: "subject",
      entityId: data.subject.id,
      config: config,
      submitData: result,
      successHandler: () => {
        reloadTable();
      },
      failHandler: (error: any) => {
        handleError(error);
      },
    });
  };

  const getBuildingCsvUrl = (url: string) => {
    if (subdomain !== "admin" || !subjectId) {
      return url;
    }

    const params = new URLSearchParams({
      impersonate_subject: subjectId,
    });
    return `${url}?${params.toString()}`;
  };

  const downloadExampleCsv = async () => {
    try {
      const response = await fetch(
        getBuildingCsvUrl("/api/v1/building/example_csv/"),
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        const result = await response.json();
        const errorContent = result
          ? result
          : t("error_downloading_example_csv");
        throw errorContent;
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = t("example_building_import_csv_filename");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      handleError(error);
      setNotificationMessage(t("download_failed"));
      setNotificationVariant("error");
      setShowNotification(true);
    }
  };

  const handleCsvUpload = async (
    files: File[],
    { onUploadProgress }: FileUploadRequestOptions,
  ) => {
    for (const file of files) {
      const formData = new FormData();
      if (subdomain === "admin" && subjectId) {
        formData.append("impersonate_subject", subjectId);
      }
      formData.append("file", file);
      const response = await apiRequest<Record<string, any>>({
        entity: "building",
        additionalRouteParts: { create_from_csv: "/" },
        method: "post",
        submitData: formData,
        shouldNotImpersonate: true,
        disableGlobalSpinner: true,
        config: {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress,
        },
      });
      const result = response.data;

      setEntries({
        created: result["created entries"] || [],
        updated: result["updated entries"] || [],
        skipped: result["skipped entries"] || [],
      });

      setEntriesModalOpen(true);
      setNotificationMessage(t("upload_successful"));
      setNotificationVariant("success");
      setShowNotification(true);
      setRefreshTable((prev: boolean) => !prev);
    }
  };

  const getCsvUploadErrorMessages = (entry: any): string => {
    if (entry?.fields && typeof entry.fields === "object") {
      const allErrors: string[] = [];
      const fieldLabelKeys: Record<string, string> = {
        name: "building_name",
        street_house: "building_street_house",
        zip: "building_zip",
        city: "building_city",
        contact_name: "building_contact_name",
        contact_email: "building_contact_email",
        contact_phone: "building_contact_phone",
        parking_situation: "building_parking_situation",
        additional_information: "building_additional_information",
      };
      Object.keys(entry.fields).forEach((fieldName) => {
        const fieldErrors = entry.fields[fieldName];
        const fieldLabel = fieldLabelKeys[fieldName];
        const formatError = (message: string) =>
          fieldLabel ? `${t(fieldLabel)}: ${message}` : message;
        if (Array.isArray(fieldErrors)) {
          allErrors.push(...fieldErrors.map(formatError));
        } else if (fieldErrors) {
          allErrors.push(formatError(String(fieldErrors)));
        }
      });
      if (allErrors.length > 0) {
        return allErrors.join("; ");
      }
    }
    if (entry?.reason) {
      return entry.reason;
    }
    return t("unknown_reason");
  };

  const entriesModalContent = (
    <div className={styles.entriesModalContainer}>
      <section className={styles.entriesSection}>
        <p className={styles.boldText}>{t("created_entries")}:</p>
        <span>{entries?.created?.length || 0}</span>
      </section>
      <section className={styles.entriesSection}>
        <p className={styles.boldText}>{t("updated_entries")}:</p>
        <span>{entries?.updated?.length || 0}</span>
      </section>
      <section className={styles.entriesSectionColumn}>
        <p className={styles.incorrectEntriesText}>{t("incorrect_entries")}:</p>
        <List>
          {entries?.skipped?.map((entry: any, index: number) => (
            <ListItem key={index} className={styles.errorListItem}>
              <ListItemText
                primary={entry?.name || entry?.email || t("unknown_entry")}
                secondary={getCsvUploadErrorMessages(entry)}
                secondaryTypographyProps={{ color: "red" }}
              />
            </ListItem>
          ))}
        </List>
      </section>
    </div>
  );

  const customActionButton = (
    <div className={styles.customActionButtonWrapper}>
      <GenericButton onClick={() => setCsvUploadModalOpen(true)}>
        {t("csv_import")}
      </GenericButton>
      <GenericButton onClick={downloadExampleCsv}>
        {t("csv_import_sample_file")}
      </GenericButton>
    </div>
  );

  const resetStateOnCloseNotification = () => {
    setShowNotification(false);
  };

  let heading = t("buildings");
  if (companyName !== "") {
    heading = t("buildings") + ` - ${companyName}`;
  }
  usePageHeading(hideTableHeader ? undefined : heading);

  return (
    <>
      {!hideBackButton && subjectId && (
        <Tooltip placement="right" title={t("back_to", { name: t("company") })}>
          <GenericButton
            variant="icon"
            color="iconBase"
            onClick={handleNavigateBack}
            className={styles.backButton}
          >
            <ArrowBackIcon />
          </GenericButton>
        </Tooltip>
      )}
      <GenericCrudTable
        readOnlyMode={isDepartmentManager}
        allowColumnFiltering={true}
        showColumnFilterbyDefault={true}
        companySubjectId={subjectId}
        entity="building"
        entityAdapter={buildingEntityAdapter}
        additionalUrlParameter={additionalUrlParameters}
        rowActions={buildingRowActions}
        refreshTable={refreshTable}
        customTopToolBarActionButton={customActionButton}
        columns={[
          {
            header: t("id"),
            accessorKey: "id",
            id: "id",
            hideField: true,
            enableHiding: !subjectId ? false : true,
            visibleInShowHideMenu: !subjectId ? false : true,
            enableGlobalFilter: false,
          },
          {
            header: t("building_name"),
            accessorKey: "name",
            id: "name",
            enableForCreate: true,
            required: true,
          },
          {
            header: t("building_street_house"),
            accessorKey: "street_house",
            id: "street_house",
            enableForCreate: true,
            required: true,
          },
          {
            header: t("building_zip"),
            accessorKey: "zip",
            id: "zip",
            enableForCreate: true,
            required: true,
          },
          {
            header: t("building_city"),
            accessorKey: "city",
            id: "city",
            enableForCreate: true,
            required: true,
          },
          {
            header: t("building_contact_name"),
            accessorKey: "contact_name",
            id: "contact_name",
            enableForCreate: true,
          },
          {
            header: t("building_contact_email"),
            accessorKey: "contact_email",
            customColumnFilterId: "contact_email",
            customFilterParameter: "__icontains",
            id: "contact_email",
            enableForCreate: true,
          },
          {
            header: t("building_contact_phone"),
            accessorKey: "contact_phone",
            id: "contact_phone",
            enableForCreate: true,
          },
          {
            header: t("building_parking_situation"),
            accessorKey: "parking_situation",
            id: "parking_situation",
            enableForCreate: true,
          },
          {
            header: t("building_additional_information"),
            accessorKey: "additional_information",
            id: "additional_information",
            enableForCreate: true,
            textarea: true,
          },
          ...(subdomain === "admin" 
            ? getRecordMetadataColumns(t, [
                "created_on",
                "created_by",
                "modified_on",
                "modified_by",
              ])
            : []),
        ]}
      />
      {errorModal}
      <Modal
        open={csvUploadModalOpen}
        onClose={() => setCsvUploadModalOpen(false)}
        title={t("csv_import")}
        hideDialogActions={true}
        disableCancelButton={csvUploadInProgress}
        maxWidth="40rem"
      >
        <FileUploadInput
          multiple={false}
          name="building_csv"
          folderId="building"
          onCancel={() => setCsvUploadModalOpen(false)}
          customUploadHandler={handleCsvUpload}
          accept={[".csv"]}
          onUploadStateChange={setCsvUploadInProgress}
          handleUploadSuccess={(modalOpen: boolean) => {
            if (modalOpen) {
              setCsvUploadModalOpen(false);
            }
          }}
        />
      </Modal>
      <Modal
        open={entriesModalOpen}
        onSubmit={() => setEntriesModalOpen(false)}
        title={t("overview")}
        submitButtonText={t("confirm")}
        buttonPlacement="flex-end"
        hideCancelButton
      >
        {entriesModalContent}
      </Modal>
      <GenericNotification
        message={showNotification ? notificationMessage : null}
        variant={notificationVariant}
        handleCloseSnackbar={resetStateOnCloseNotification}
      />
    </>
  );
};

export default BuildingTable;
